import React, { useState, useEffect } from 'react';
import CountryEmoji from './CountryEmoji';
import Typography from '@mui/material/Typography';
import {
  imageProxy,
  ccFromSlug,
  nameFromSlug,
  slugFromCC,
} from 'tf-post-parser';
import emojiFlags from 'emoji-flags';
import Head from '../Header/Head';
import Image from 'components/FutureImage';
import CountryMapPosts from './CountryMapPosts';
import PostGrid from 'components/Grid/PostGrid';
import { makeStyles } from 'tss-react/mui';
import Link from '@mui/material/Link';
import BlurHashCanvas from 'components/General/BlurHashCanvas';
import { MAPBOX_TOKEN } from '../../config';
import ChatbotBadges from './ChatbotBadges';
import Markdown from 'markdown-to-jsx';
import EditLocationDetails from 'components/General/EditLocationDetails';
import addDepositphotosLink from 'helpers/addDepositphotosLink';
import imageCrop from 'helpers/imageCrop';

const useStyles = makeStyles()(theme => ({
  root: {
    background: theme.palette.background.default,
  },
}));

export default function MiniTravelGuide(props) {
  const [bbox, setBbox] = useState(undefined);

  const { title, locationDetails, body, query, noIndex, breadcrumbs } = props;
  let { locationBox, subdivision, city } = query;
  if (!subdivision) subdivision = props.subdivision;
  if (!city) city = props.city;
  const country = query.country || slugFromCC(query.countryCode);
  const countryCode = query.countryCode || ccFromSlug(country);
  const countryName = nameFromSlug(country);

  const { classes } = useStyles();

  const locationString = `${city ? `${city}, ` : ''}${
    subdivision ? `${subdivision}, ` : ''
  }${countryName}`;

  const generatedOgImage = `https://tfog.b-cdn.net/api/travelGuide?title=${encodeURIComponent(
    title,
  )}&image=${imageCrop(locationDetails.image, 1920, 1200)}`;

  useEffect(() => {
    if (!locationBox) {
      fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          locationString,
        )}.json?limit=1&language=en-GB&access_token=${MAPBOX_TOKEN}`,
      )
        .then(res => {
          return res.json();
        })
        .then(data => {
          if (data && data.features && data.features.length > 0) {
            setBbox(data.features[0].bbox);
          }
        });
    }
  }, [city, subdivision, countryName]);

  return (
    <>
      <Head
        shorttitle={`${
          emojiFlags.countryCode(countryCode.toUpperCase()).emoji
        } ${title} Travel Guide (${new Date().getFullYear()})`}
        description={`The ultimate travel guide to ${title}: Your all-in-one travel resource for insider tips, itineraries, must-sees and hidden gems in ${title}.`}
        image={generatedOgImage}
        includeMapbox
        noIndex={noIndex}
        breadcrumbs={breadcrumbs}
        // breadcrumbs={breadcrumbs} // TODO: Add region to breadcrumbs, create region pages/travel guides
      />
      <div className="tw-fixed tw-h-screen tw-w-full">
        <div className="tw-h-screen tw-relative -tw-mt-[65px]">
          <BlurHashCanvas
            hash={locationDetails.blurHash}
            width={1920}
            height={1200}
          />
          <Image
            alt={title}
            src={imageProxy(locationDetails.image, 1920, 1200)}
            layout="fill"
            className="tw-brightness-50 tw-object-cover tw-object-center"
          />
        </div>
      </div>
      <div className="tw-z-10 tw-relative">
        <div className="tw-container tw-mx-auto tw-pt-2 tw-pb-4">
          <div className="tw-text-base">
            <div className="pb-2">
              {countryCode && <CountryEmoji countryCode={countryCode} isFlag />}
            </div>
            {subdivision && (
              <div className="tw-text-primary-100 tw-font-semibold text-center">
                <Link
                  href="/destinations"
                  className="tw-text-primary-100 hover:tw-underline"
                >
                  Travel Guides
                </Link>{' '}
                &gt;{' '}
                <Link
                  href={`/destinations/${country}`}
                  className="tw-text-primary-100 hover:tw-underline"
                >
                  {countryName}
                </Link>{' '}
                {city && (
                  <>
                    &gt;{' '}
                    <Link
                      className="tw-text-primary-100 hover:tw-underline"
                      href={`/destinations/${country}/${encodeURIComponent(
                        subdivision,
                      )}`}
                    >
                      {subdivision}
                    </Link>
                  </>
                )}{' '}
              </div>
            )}
            <Typography
              gutterBottom
              variant="h2"
              component="h1"
              className="text-light font-weight-bold text-center tw-p-2"
              style={{
                textShadow: '1px 1px 10px #343A40',
              }}
            >
              {title} Travel Guide
              {!props.noEdit && (
                <EditLocationDetails
                  countryCode={countryCode}
                  subdivision={subdivision}
                  city={city}
                  data={locationDetails}
                />
              )}
            </Typography>
            <div className="md:tw-grid md:tw-grid-cols-3 tw-gap-4">
              {[
                {
                  title: `What's unique?`,
                  content: (
                    <div className="tw-text-gray-200">
                      {body && body !== ' ' ? (
                        <Markdown
                          options={{
                            overrides: {
                              a: {
                                component: props => {
                                  return props.children;
                                },
                              },
                              img: {
                                component: props => {
                                  return props.alt;
                                },
                              },
                            },
                          }}
                        >
                          {body}
                        </Markdown>
                      ) : (
                        <></>
                      )}
                    </div>
                  ),
                },
                // {
                //   title: `Best Experiences`,
                //   content: (
                //     <>
                //       {(center || locationBox) && (
                //         <DestinationActivities
                //           center={center}
                //           bbox={locationBox}
                //           setChatMessage={props.setChatMessage}
                //         />
                //       )}
                //     </>
                //     // <div className="tw-text-gray-200">Hotels: Booking.com</div>
                //   ),
                // },
              ].map(({ title, content }, i) => {
                return (
                  <div
                    key={i}
                    className={`tw-mt-1 tw-mx-1 md:tw-mt-0 md:tw-mx-0 tw-row-span-1 tw-rounded-xl tw-col-span-3 lg:tw-col-span-2 tw-bg-white tw-bg-clip-padding tw-backdrop-filter tw-backdrop-blur-md tw-bg-opacity-25 tw-border-2 tw-border-gray-200 tw-p-4`}
                  >
                    <h2 className="text-white tw-text-2xl">{title}</h2>
                    {content}
                  </div>
                );
              })}
              <ChatbotBadges
                setChatMessage={props.setChatMessage}
                locationString={locationString}
              />
              {(locationBox || bbox || locationDetails.bbox) && (
                <CountryMapPosts
                  bbox={locationBox || bbox || locationDetails.bbox}
                  countryCode={countryCode}
                />
              )}
            </div>
          </div>
          <div className="tw-pt-2 tw-flex tw-w-full tw-text-xs tw-text-gray-300">
            <div className="tw-pl-1 tw-w-1/2">
              Disclosure: This travel guide may contain{' '}
              <Link
                href="/about/affiliate-disclosure"
                className="tw-text-primary-200 hover:tw-underline"
              >
                affiliate links
              </Link>
            </div>
            {locationDetails &&
              (locationDetails.attribution || locationDetails.coverAlt) && (
                <>
                  <div className="tw-text-right tw-w-1/2 tw-pr-1">
                    {locationDetails.coverAlt && (
                      <>Cover: {locationDetails.coverAlt}</>
                    )}
                    {locationDetails.coverAlt &&
                      locationDetails.attribution && <>; </>}
                    {locationDetails.attribution && (
                      <>
                        Photo Credit:{' '}
                        {(locationDetails && locationDetails.unsplashUser && (
                          <a
                            className="text-decoration-underline"
                            target="_blank"
                            rel="nofollow noreferrer noopener"
                            href={`https://unsplash.com/@${
                              locationDetails && locationDetails.unsplashUser
                            }?utm_source=TravelFeed&utm_medium=referral`}
                          >
                            {locationDetails && locationDetails.attribution}
                          </a>
                        )) || (
                          <span>
                            {addDepositphotosLink(locationDetails.attribution)}
                          </span>
                        )}{' '}
                        {locationDetails && locationDetails.unsplashUser && (
                          <span>
                            /{' '}
                            <a
                              target="_blank"
                              rel="nofollow noreferrer noopener"
                              href="https://unsplash.com/?utm_source=TravelFeed&utm_medium=referral"
                              className="text-decoration-underline"
                            >
                              Unsplash
                            </a>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
          </div>
        </div>
        <div className={classes.root}>
          <Typography
            gutterBottom
            variant="h4"
            component="h2"
            className="pt-4 text-center tw-font-semibold"
            color="textPrimary"
          >
            {locationDetails.title} Travel Blogs
          </Typography>
          <div className="container pb-4 containerInvisibleOnMobile">
            <PostGrid
              active="destination"
              query={query}
              grid={{ lg: 4, md: 4, sm: 6, xs: 12 }}
              cardHeight={200}
              poststyle="grid"
            />
          </div>
        </div>
      </div>
      <style jsx global>{`
        .linkUnderline a {
          text-decoration: underline !important;
          color: #80cbc4;
        }
        .linkUnderline a:hover {
          color: #26a69a;
        }
      `}</style>
    </>
  );
}
